import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import dayjs from "dayjs";

export type ContractEndingRow = {
  id: string | number;
  title: string;
  sublabel?: string;
  endDate: string;
};

type Props = {
  rows: ContractEndingRow[];
  emptyLabel?: string;
  daysLeftLabel?: string;
  onClickVehicle?: (id: string | number) => void;
};

/** Urgency thresholds (in days) driving the badge color. */
const URGENT_DAYS = 30;
const WARNING_DAYS = 90;

/**
 * List of vehicle contracts ending within the selected lookahead window
 * (see the months selector passed via ChartCard's `actions`). Sorted
 * soonest-first; badge color escalates as the end date approaches.
 */
export const ContractEndingsCard: React.FC<Props> = ({
  rows,
  emptyLabel = "No contract ending soon",
  daysLeftLabel = "days left",
  onClickVehicle,
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";

  if (!rows.length) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100%", minHeight: 160 }}>
        <Typography sx={{ color: "text.disabled", fontSize: 13 }}>{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1} sx={{ maxHeight: 280, overflowY: "auto", pr: 0.5 }}>
      {rows.map((row) => {
        const daysLeft = dayjs(row.endDate).startOf("day").diff(dayjs().startOf("day"), "day");
        const key = daysLeft <= URGENT_DAYS ? "error" : daysLeft <= WARNING_DAYS ? "warning" : "primary";
        const main = theme.palette[key].main;
        const text = isLight ? theme.palette[key].dark : theme.palette[key].light;
        return (
          <Stack
            key={row.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              transition: "background-color 160ms ease",
              cursor: onClickVehicle ? "pointer" : "default",
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={() => onClickVehicle?.(row.id)}
          >
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "text.primary" }}>
                {row.title}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                {row.sublabel} · {dayjs(row.endDate).format("DD/MM/YYYY")}
              </Typography>
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 700,
                px: 1,
                py: 0.4,
                borderRadius: 1.5,
                color: text,
                bgcolor: alpha(main, isLight ? 0.13 : 0.2),
                whiteSpace: "nowrap",
              }}
            >
              {daysLeft >= 0 ? daysLeft : 0} {daysLeftLabel}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};
